/**
 * Facebook Pixel Utility Module
 * 
 * Handles dynamic loading and initialization of Facebook Pixel
 * for tracking page views and custom events.
 */

// Track initialization state
let isPixelInitialized = false;
let currentPixelId = null;
let scriptLoadPromise = null;

/**
 * Load the Facebook Pixel base code (script)
 * @returns {Promise<void>}
 */
const loadFacebookPixelScript = () => {
  // Return existing promise if already loading
  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  // Check if script already exists
  if (document.getElementById('facebook-pixel-script')) {
    scriptLoadPromise = Promise.resolve();
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    try {
      // Initialize fbq function before loading script
      if (!window.fbq) {
        window.fbq = function () {
          window.fbq.callMethod
            ? window.fbq.callMethod.apply(window.fbq, arguments)
            : window.fbq.queue.push(arguments);
        };

        if (!window._fbq) {
          window._fbq = window.fbq;
        }

        window.fbq.push = window.fbq;
        window.fbq.loaded = true;
        window.fbq.version = '2.0';
        window.fbq.queue = [];
      }

      // Create and inject the script
      const script = document.createElement('script');
      script.id = 'facebook-pixel-script';
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';

      script.onload = () => {
        if (process.env.NODE_ENV === 'development') {
          console.log('[Facebook Pixel] Script loaded successfully');
        }
        resolve();
      };

      script.onerror = (error) => {
        if (process.env.NODE_ENV === 'development') {
          console.error('[Facebook Pixel] Failed to load script:', error);
        }
        // Clean up on error
        const existingScript = document.getElementById('facebook-pixel-script');
        if (existingScript) {
          existingScript.remove();
        }
        scriptLoadPromise = null;
        reject(new Error('Failed to load Facebook Pixel script'));
      };

      // Insert script into head
      const firstScript = document.getElementsByTagName('script')[0];
      if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        document.head.appendChild(script);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[Facebook Pixel] Error during script loading:', error);
      }
      scriptLoadPromise = null;
      reject(error);
    }
  });

  return scriptLoadPromise;
};

/**
 * Initialize Facebook Pixel with the given ID
 * @param {string} pixelId - The Facebook Pixel ID
 * @returns {Promise<boolean>} - True if initialized successfully
 */
export const initFacebookPixel = async (pixelId) => {
  // Validate pixel ID
  if (!pixelId || typeof pixelId !== 'string' || pixelId.trim() === '') {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Facebook Pixel] No valid Pixel ID provided, skipping initialization');
    }
    return false;
  }

  const trimmedPixelId = pixelId.trim();

  // Check if already initialized with the same ID
  if (isPixelInitialized && currentPixelId === trimmedPixelId) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Facebook Pixel] Already initialized with ID:', trimmedPixelId);
    }
    return true;
  }

  // If initialized with different ID, we need to reinitialize
  if (isPixelInitialized && currentPixelId !== trimmedPixelId) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Facebook Pixel] Reinitializing with new ID:', trimmedPixelId);
    }
    // Reset state for new initialization
    isPixelInitialized = false;
  }

  try {
    // Load the script first
    await loadFacebookPixelScript();

    // Wait for fbq to be ready
    if (window.fbq) {
      // Initialize the pixel
      window.fbq('init', trimmedPixelId);

      // Track initial PageView
      window.fbq('track', 'PageView');

      isPixelInitialized = true;
      currentPixelId = trimmedPixelId;

      if (process.env.NODE_ENV === 'development') {
        console.log('[Facebook Pixel] Initialized successfully with ID:', trimmedPixelId);
      }
      return true;
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.error('[Facebook Pixel] fbq not available after script load');
      }
      return false;
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Facebook Pixel] Initialization error:', error);
    }
    return false;
  }
};

/**
 * Track a PageView event
 * Used for SPA route changes
 */
export const trackPageView = () => {
  if (!isPixelInitialized || !window.fbq) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Facebook Pixel] Cannot track PageView - not initialized');
    }
    return;
  }

  try {
    window.fbq('track', 'PageView');
    if (process.env.NODE_ENV === 'development') {
      console.log('[Facebook Pixel] PageView tracked');
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Facebook Pixel] Error tracking PageView:', error);
    }
  }
};

/**
 * Track a Lead event
 * Use when a user submits a contact or booking form
 * @param {Object} data - Optional lead data
 * @param {string} data.content_name - Name/description of the lead
 * @param {string} data.content_category - Category of the lead
 * @param {number} data.value - Value of the lead
 * @param {string} data.currency - Currency code (e.g., 'EGP', 'USD')
 */
export const trackLead = (data = {}) => {
  if (!isPixelInitialized || !window.fbq) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Facebook Pixel] Cannot track Lead - not initialized');
    }
    return;
  }

  try {
    window.fbq('track', 'Lead', {
      content_name: data.content_name || 'Lead Form Submission',
      content_category: data.content_category || 'Lead',
      value: data.value || 0,
      currency: data.currency || 'EGP',
      ...data
    });
    if (process.env.NODE_ENV === 'development') {
      console.log('[Facebook Pixel] Lead tracked:', data);
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Facebook Pixel] Error tracking Lead:', error);
    }
  }
};

/**
 * Track a CompleteRegistration event
 * Use when a user completes account registration
 * @param {Object} data - Optional registration data
 * @param {string} data.content_name - Registration type/name
 * @param {string} data.status - Registration status
 * @param {number} data.value - Value of the registration
 * @param {string} data.currency - Currency code
 */
export const trackCompleteRegistration = (data = {}) => {
  if (!isPixelInitialized || !window.fbq) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Facebook Pixel] Cannot track CompleteRegistration - not initialized');
    }
    return;
  }

  try {
    window.fbq('track', 'CompleteRegistration', {
      content_name: data.content_name || 'User Registration',
      status: data.status || 'completed',
      value: data.value || 0,
      currency: data.currency || 'EGP',
      ...data
    });
    if (process.env.NODE_ENV === 'development') {
      console.log('[Facebook Pixel] CompleteRegistration tracked:', data);
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Facebook Pixel] Error tracking CompleteRegistration:', error);
    }
  }
};

/**
 * Track a Purchase event
 * Use when a user completes a payment/purchase
 * @param {Object} data - Purchase data (required)
 * @param {number} data.value - Purchase value (required)
 * @param {string} data.currency - Currency code (required, defaults to 'EGP')
 * @param {Array} data.contents - Array of purchased items
 * @param {string} data.content_type - Type of content (e.g., 'product', 'service')
 * @param {number} data.num_items - Number of items purchased
 */
export const trackPurchase = (data = {}) => {
  if (!isPixelInitialized || !window.fbq) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Facebook Pixel] Cannot track Purchase - not initialized');
    }
    return;
  }

  if (typeof data.value !== 'number' || data.value <= 0) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Facebook Pixel] Purchase value is required and must be positive');
    }
    return;
  }

  try {
    window.fbq('track', 'Purchase', {
      value: data.value,
      currency: data.currency || 'EGP',
      content_type: data.content_type || 'service',
      num_items: data.num_items || 1,
      ...data
    });
    if (process.env.NODE_ENV === 'development') {
      console.log('[Facebook Pixel] Purchase tracked:', data);
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Facebook Pixel] Error tracking Purchase:', error);
    }
  }
};

/**
 * Track an InitiateCheckout event
 * Use when a user starts the checkout/booking process
 * @param {Object} data - Checkout data
 * @param {number} data.value - Cart/booking value
 * @param {string} data.currency - Currency code
 * @param {number} data.num_items - Number of items
 */
export const trackInitiateCheckout = (data = {}) => {
  if (!isPixelInitialized || !window.fbq) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Facebook Pixel] Cannot track InitiateCheckout - not initialized');
    }
    return;
  }

  try {
    window.fbq('track', 'InitiateCheckout', {
      value: data.value || 0,
      currency: data.currency || 'EGP',
      num_items: data.num_items || 1,
      ...data
    });
    if (process.env.NODE_ENV === 'development') {
      console.log('[Facebook Pixel] InitiateCheckout tracked:', data);
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Facebook Pixel] Error tracking InitiateCheckout:', error);
    }
  }
};

/**
 * Track a Contact event
 * Use when a user contacts the business
 */
export const trackContact = () => {
  if (!isPixelInitialized || !window.fbq) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Facebook Pixel] Cannot track Contact - not initialized');
    }
    return;
  }

  try {
    window.fbq('track', 'Contact');
    if (process.env.NODE_ENV === 'development') {
      console.log('[Facebook Pixel] Contact tracked');
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Facebook Pixel] Error tracking Contact:', error);
    }
  }
};

/**
 * Track a custom event
 * @param {string} eventName - Name of the custom event
 * @param {Object} data - Custom event data
 */
export const trackCustomEvent = (eventName, data = {}) => {
  if (!isPixelInitialized || !window.fbq) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Facebook Pixel] Cannot track custom event - not initialized');
    }
    return;
  }

  if (!eventName || typeof eventName !== 'string') {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Facebook Pixel] Event name is required');
    }
    return;
  }

  try {
    window.fbq('trackCustom', eventName, data);
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Facebook Pixel] Custom event "${eventName}" tracked:`, data);
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error(`[Facebook Pixel] Error tracking custom event "${eventName}":`, error);
    }
  }
};

/**
 * Remove the Facebook Pixel
 * Use when admin clears the Pixel ID
 */
export const removeFacebookPixel = () => {
  // Remove the script
  const script = document.getElementById('facebook-pixel-script');
  if (script) {
    script.remove();
  }

  // Remove noscript (if any)
  const noscripts = document.querySelectorAll('noscript[data-fb-pixel]');
  noscripts.forEach(ns => ns.remove());

  // Reset state
  isPixelInitialized = false;
  currentPixelId = null;
  scriptLoadPromise = null;

  // Clear fbq
  if (window.fbq) {
    window.fbq = undefined;
    window._fbq = undefined;
  }

  if (process.env.NODE_ENV === 'development') {
    console.log('[Facebook Pixel] Removed');
  }
};

/**
 * Check if pixel is initialized
 * @returns {boolean}
 */
export const isPixelActive = () => {
  return isPixelInitialized && currentPixelId !== null;
};

/**
 * Get current pixel ID
 * @returns {string|null}
 */
export const getCurrentPixelId = () => {
  return currentPixelId;
};

// Export default object with all methods
const facebookPixel = {
  init: initFacebookPixel,
  trackPageView,
  trackLead,
  trackCompleteRegistration,
  trackPurchase,
  trackInitiateCheckout,
  trackContact,
  trackCustomEvent,
  remove: removeFacebookPixel,
  isActive: isPixelActive,
  getCurrentId: getCurrentPixelId
};

export default facebookPixel;